import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import type { TourWithSteps, TourStep } from "@shared/schema";
import { getCategoryFromTargetPage, TOUR_CATEGORY_LABELS } from "@shared/schema";
import { Plus, Edit, Trash2, Eye, Save, X, Sparkles, ArrowUp, ArrowDown, Crosshair } from "lucide-react";
import { format } from "date-fns";
import { useElementPicker } from "@/components/element-picker-context";

export default function AdminToursPage() {
  const { toast } = useToast();
  const [selectedTour, setSelectedTour] = useState<TourWithSteps | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isTourDialogOpen, setIsTourDialogOpen] = useState(false);
  const [isStepDialogOpen, setIsStepDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<TourStep | null>(null);
  const [selectorValue, setSelectorValue] = useState("");
  const selectorInputRef = useRef<HTMLInputElement>(null);
  
  const { pickerResult, session, startPicker, clearResult } = useElementPicker();

  const { data: tours = [], isLoading } = useQuery<TourWithSteps[]>({
    queryKey: ["/api/tours"],
  });

  useEffect(() => {
    if (pickerResult && session) {
      setSelectorValue(pickerResult);
      if (selectorInputRef.current) {
        selectorInputRef.current.value = pickerResult;
      }
      setIsStepDialogOpen(true);
      if (session.isEditing && session.editingStepId) {
        const step = selectedTour?.steps.find(s => s.id === session.editingStepId);
        if (step) setEditingStep(step);
      }
      clearResult();
    }
  }, [pickerResult, session, selectedTour, clearResult]);

  useEffect(() => {
    if (editingStep) {
      setSelectorValue(editingStep.targetSelector || "");
    } else {
      setSelectorValue("");
    }
  }, [editingStep]);

  const createTourMutation = useMutation({
    mutationFn: async (data: {
      title: string;
      description: string;
      targetPage: string;
      difficulty: string;
      estimatedMinutes: number;
    }) => {
      const response = await fetch("/api/tours", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to create tour");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tours"] });
      setIsCreateDialogOpen(false);
      toast({ title: "Success", description: "Tour created successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create tour", variant: "destructive" });
    },
  });

  const updateTourMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await fetch(`/api/tours/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to update tour");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tours"] });
      toast({ title: "Success", description: "Tour updated successfully" });
    },
  });

  const deleteTourMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/tours/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to delete tour");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tours"] });
      setSelectedTour(null);
      setIsTourDialogOpen(false);
      toast({ title: "Success", description: "Tour deleted successfully" });
    },
  });

  const createStepMutation = useMutation({
    mutationFn: async (data: {
      tourId: string;
      title: string;
      description: string;
      videoUrl?: string;
      targetSelector?: string;
    }) => {
      const response = await fetch(`/api/tours/${data.tourId}/steps`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to create step");
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/tours"] });
      // Refresh selectedTour from updated query data
      const updatedTours = queryClient.getQueryData<TourWithSteps[]>(["/api/tours"]);
      if (selectedTour && updatedTours) {
        const updatedTour = updatedTours.find(t => t.id === selectedTour.id);
        if (updatedTour) {
          setSelectedTour(updatedTour);
        }
      }
      setIsStepDialogOpen(false);
      setEditingStep(null);
      toast({ title: "Success", description: "Step created successfully" });
    },
  });

  const updateStepMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await fetch(`/api/tour-steps/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to update step");
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/tours"] });
      // Refresh selectedTour from updated query data
      const updatedTours = queryClient.getQueryData<TourWithSteps[]>(["/api/tours"]);
      if (selectedTour && updatedTours) {
        const updatedTour = updatedTours.find(t => t.id === selectedTour.id);
        if (updatedTour) {
          setSelectedTour(updatedTour);
        }
      }
      setIsStepDialogOpen(false);
      setEditingStep(null);
      toast({ title: "Success", description: "Step updated successfully" });
    },
  });

  const deleteStepMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/tour-steps/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to delete step");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/tours"] });
      // Refresh selectedTour from updated query data
      const updatedTours = queryClient.getQueryData<TourWithSteps[]>(["/api/tours"]);
      if (selectedTour && updatedTours) {
        const updatedTour = updatedTours.find(t => t.id === selectedTour.id);
        if (updatedTour) {
          setSelectedTour(updatedTour);
        }
      }
      toast({ title: "Success", description: "Step deleted successfully" });
    },
  });

  const handleCreateTour = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    createTourMutation.mutate({
      title: formData.get("title") as string,
      description: formData.get("description") as string,
      targetPage: formData.get("targetPage") as string,
      difficulty: formData.get("difficulty") as string,
      estimatedMinutes: parseInt(formData.get("estimatedMinutes") as string),
    });
  };

  const handleCreateStep = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    if (editingStep) {
      updateStepMutation.mutate({
        id: editingStep.id,
        data: {
          title: formData.get("title") as string,
          description: formData.get("description") as string,
          videoUrl: formData.get("videoUrl") as string || null,
          targetSelector: selectorValue || null,
        },
      });
    } else {
      createStepMutation.mutate({
        tourId: selectedTour!.id,
        title: formData.get("title") as string,
        description: formData.get("description") as string,
        videoUrl: formData.get("videoUrl") as string || undefined,
        targetSelector: selectorValue || undefined,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-full p-4 md:p-6">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-2">
          <Sparkles className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Tour Management</h1>
            <p className="text-muted-foreground">Create and manage guided tours for users</p>
          </div>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="button-create-tour">
          <Plus className="h-4 w-4 mr-2" />
          Create Tour
        </Button>
      </div>

      <div className="space-y-2">
        {tours.map((tour) => (
          <div 
            key={tour.id} 
            className="flex items-center gap-4 p-4 bg-card border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
            onClick={() => { setSelectedTour(tour); setIsTourDialogOpen(true); }}
            data-testid={`row-admin-tour-${tour.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium truncate">{tour.title}</span>
                <Badge variant="outline" className="shrink-0">
                  {TOUR_CATEGORY_LABELS[getCategoryFromTargetPage(tour.targetPage)] || getCategoryFromTargetPage(tour.targetPage)}
                </Badge>
                <Badge className={`shrink-0 ${
                  tour.difficulty === "beginner" ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" :
                  tour.difficulty === "intermediate" ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300" :
                  "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                }`}>
                  {tour.difficulty}
                </Badge>
              </div>
              {tour.description && (
                <p className="text-sm text-muted-foreground truncate">{tour.description}</p>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground shrink-0">
              <span>{(tour.steps || []).length} steps</span>
              <span>{tour.estimatedMinutes} min</span>
              {tour.createdAt && (
                <span className="hidden md:inline">{format(new Date(tour.createdAt), "MMM d, yyyy")}</span>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
              onClick={(e) => { 
                e.stopPropagation(); 
                deleteTourMutation.mutate(tour.id); 
              }}
              disabled={deleteTourMutation.isPending}
              data-testid={`button-delete-tour-${tour.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* Create Tour Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent data-testid="dialog-create-tour">
          <form onSubmit={handleCreateTour}>
            <DialogHeader>
              <DialogTitle>Create New Tour</DialogTitle>
              <DialogDescription>Add a new guided tour for users</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input id="title" name="title" required data-testid="input-tour-title" />
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea id="description" name="description" required data-testid="input-tour-description" />
              </div>
              <div>
                <Label htmlFor="targetPage">Target Page *</Label>
                <Select name="targetPage" required>
                  <SelectTrigger data-testid="select-tour-target-page">
                    <SelectValue placeholder="Select target page" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="/dashboard">Dashboard</SelectItem>
                    <SelectItem value="/goals">Goals</SelectItem>
                    <SelectItem value="/tasks">Tasks</SelectItem>
                    <SelectItem value="/habits">Habits</SelectItem>
                    <SelectItem value="/journal">Journal</SelectItem>
                    <SelectItem value="/notepad">Notepad</SelectItem>
                    <SelectItem value="/planner">Planner</SelectItem>
                    <SelectItem value="/calendar">Calendar</SelectItem>
                    <SelectItem value="/meditation">Meditation</SelectItem>
                    <SelectItem value="/checklists">Checklists</SelectItem>
                    <SelectItem value="/gratefuls">Gratefuls</SelectItem>
                    <SelectItem value="/admin">Admin Panel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="difficulty">Difficulty *</Label>
                <Select name="difficulty" required>
                  <SelectTrigger data-testid="select-tour-difficulty">
                    <SelectValue placeholder="Select difficulty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="estimatedMinutes">Estimated Minutes *</Label>
                <Input id="estimatedMinutes" name="estimatedMinutes" type="number" min="1" required 
                       data-testid="input-tour-minutes" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createTourMutation.isPending} data-testid="button-submit-tour">
                {createTourMutation.isPending ? "Creating..." : "Create Tour"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tour Detail Dialog */}
      {selectedTour && (
        <Dialog open={isTourDialogOpen} onOpenChange={setIsTourDialogOpen}>
          <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-tour-detail">
            <DialogHeader>
              <DialogTitle>{selectedTour.title}</DialogTitle>
              <DialogDescription>{selectedTour.description}</DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="steps" className="mt-4">
              <TabsList>
                <TabsTrigger value="steps">Steps ({(selectedTour.steps || []).length})</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>

              <TabsContent value="steps" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Tour Steps</h3>
                  <Button size="sm" onClick={() => { setEditingStep(null); setIsStepDialogOpen(true); }}
                          data-testid="button-add-step">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Step
                  </Button>
                </div>

                <div className="space-y-2">
                  {(selectedTour.steps || []).length === 0 ? (
                    <Card>
                      <CardContent className="py-8 text-center text-muted-foreground">
                        No steps yet. Add your first step to get started.
                      </CardContent>
                    </Card>
                  ) : (
                    (selectedTour.steps || []).map((step, index) => (
                      <Card key={step.id} data-testid={`card-step-${step.id}`}>
                        <CardContent className="pt-6">
                          <div className="flex items-start gap-3">
                            <div className="flex flex-col gap-1">
                              <Badge variant="secondary" className="w-fit">
                                Step {index + 1}
                              </Badge>
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold mb-1">{step.title}</h4>
                              <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                              {step.videoUrl && (
                                <Badge variant="outline" className="mr-2">
                                  <Eye className="h-3 w-3 mr-1" />
                                  Video
                                </Badge>
                              )}
                              {step.targetSelector && (
                                <Badge variant="outline">
                                  Spotlight: {step.targetSelector}
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-col gap-1">
                              <Button size="sm" variant="ghost" 
                                      onClick={() => { setEditingStep(step); setIsStepDialogOpen(true); }}
                                      data-testid={`button-edit-step-${step.id}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600"
                                      onClick={() => deleteStepMutation.mutate(step.id)}
                                      data-testid={`button-delete-step-${step.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="settings" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Tour Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span>Target Page:</span>
                      <Badge>{selectedTour.targetPage}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Difficulty:</span>
                      <Badge>{selectedTour.difficulty}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Estimated Time:</span>
                      <span>{selectedTour.estimatedMinutes} minutes</span>
                    </div>
                    <div className="pt-4 border-t">
                      <Button variant="destructive" onClick={() => deleteTourMutation.mutate(selectedTour.id)}
                              disabled={deleteTourMutation.isPending} data-testid="button-delete-tour">
                        <Trash2 className="h-4 w-4 mr-2" />
                        {deleteTourMutation.isPending ? "Deleting..." : "Delete Tour"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      )}

      {/* Step Editor Dialog */}
      <Dialog open={isStepDialogOpen} onOpenChange={setIsStepDialogOpen}>
        <DialogContent data-testid="dialog-step-editor">
          <form onSubmit={handleCreateStep}>
            <DialogHeader>
              <DialogTitle>{editingStep ? "Edit Step" : "Add New Step"}</DialogTitle>
              <DialogDescription>
                {editingStep ? "Update step details" : "Add a new step to this tour"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="step-title">Title *</Label>
                <Input id="step-title" name="title" defaultValue={editingStep?.title} required 
                       data-testid="input-step-title" />
              </div>
              <div>
                <Label htmlFor="step-description">Description *</Label>
                <Textarea id="step-description" name="description" defaultValue={editingStep?.description} 
                          required data-testid="input-step-description" rows={4} />
              </div>
              <div>
                <Label htmlFor="step-videoUrl">Video URL (YouTube/Vimeo)</Label>
                <Input id="step-videoUrl" name="videoUrl" defaultValue={editingStep?.videoUrl || ""} 
                       placeholder="https://youtube.com/watch?v=..." data-testid="input-step-video" />
              </div>
              <div>
                <Label htmlFor="step-targetSelector">Target Selector (CSS or data-testid)</Label>
                <div className="flex gap-2">
                  <Input 
                    id="step-targetSelector" 
                    name="targetSelector" 
                    ref={selectorInputRef}
                    value={selectorValue}
                    onChange={(e) => setSelectorValue(e.target.value)}
                    placeholder='[data-testid="button-create-goal"]' 
                    data-testid="input-step-selector" 
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (selectedTour) {
                        startPicker({
                          tourId: selectedTour.id,
                          tourTitle: selectedTour.title,
                          targetPage: selectedTour.targetPage,
                          isEditing: !!editingStep,
                          editingStepId: editingStep?.id,
                        });
                        setIsStepDialogOpen(false);
                      }
                    }}
                    data-testid="button-pick-element"
                  >
                    <Crosshair className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  CSS selector or data-testid attribute to highlight during this step. Use the crosshair button to visually pick an element.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setIsStepDialogOpen(false); setEditingStep(null); }}>
                Cancel
              </Button>
              <Button type="submit" 
                      disabled={createStepMutation.isPending || updateStepMutation.isPending}
                      data-testid="button-submit-step">
                {createStepMutation.isPending || updateStepMutation.isPending ? "Saving..." : "Save Step"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
